import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToolkitItem } from "./catalog";
import { TechniqueCard, itemTitle, itemDescription } from "./TechniqueCard";
import "./Toolkit.css";

type SourceFilter = "all" | "mine" | "library";
type ModalityFilter = "all" | "transcript" | "audio" | "vision";

// Accent-insensitive fold (same approach as ExperientialPanel's search).
function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function matchesModality(item: ToolkitItem, f: ModalityFilter): boolean {
  if (f === "all") return true;
  if (item.kind === "prompt") return f === "transcript";
  if (f === "transcript") return false;
  return item.modality === f || item.modality === "both";
}

export const TechniqueGrid: React.FC<{
  items: ToolkitItem[];
  onOpen: (item: ToolkitItem) => void;
}> = ({ items, onOpen }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");
  const [modality, setModality] = useState<ModalityFilter>("all");

  const visible = useMemo(() => {
    const q = fold(query.trim());
    return items.filter((item) => {
      if (source === "mine" && item.kind !== "prompt") return false;
      if (source === "library" && item.kind !== "experiential") return false;
      if (!matchesModality(item, modality)) return false;
      if (!q) return true;
      return fold(`${itemTitle(item, t)} ${itemDescription(item, t)}`).includes(q);
    });
  }, [items, query, source, modality, t]);

  const chip = (active: boolean, label: string, select: () => void, deselect?: () => void) => (
    <button
      type="button"
      className={`tk-chip${active ? " tk-chip--on" : ""}`}
      aria-pressed={active}
      onClick={() => (active ? deselect?.() : select())}
    >
      {label}
    </button>
  );

  return (
    <div className="tk-grid-wrap">
      <div className="tk-toolbar">
        <input
          className="tk-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("toolkit.searchPlaceholder")}
          aria-label={t("toolkit.searchPlaceholder")}
        />
        <div className="tk-chips" role="group" aria-label={t("toolkit.filterSource")}>
          {chip(source === "all", t("toolkit.filterAll"), () => setSource("all"))}
          {chip(source === "mine", t("toolkit.filterMine"), () => setSource("mine"), () => setSource("all"))}
          {chip(source === "library", t("toolkit.filterLibrary"), () => setSource("library"), () => setSource("all"))}
        </div>
        <div className="tk-chips" role="group" aria-label={t("toolkit.filterModality")}>
          {chip(modality === "all", t("toolkit.filterAnyInput"), () => setModality("all"))}
          {chip(modality === "transcript", t("toolkit.filterTranscript"), () => setModality("transcript"), () => setModality("all"))}
          {chip(modality === "audio", t("toolkit.filterAudio"), () => setModality("audio"), () => setModality("all"))}
          {chip(modality === "vision", t("toolkit.filterVision"), () => setModality("vision"), () => setModality("all"))}
        </div>
      </div>
      {visible.length === 0 ? (
        <p className="tk-empty">{t("toolkit.noMatch")}</p>
      ) : (
        <div className="tk-grid">
          {visible.map((item) => (
            <TechniqueCard key={item.id} item={item} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
};
